import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
/* eslint-disable max-len */

export interface ITicketEntityAttributes extends IModelAttributes {
	price: number;
	amount: number;

	additionalBookingOptionId?: string;
	additionalBookingOption?: Models.AdditionalBookingOptionEntity | Models.IAdditionalBookingOptionEntityAttributes;
	alterationId?: string;
	alteration?: Models.AlterationEntity | Models.IAlterationEntityAttributes;
	cargoDetailId?: string;
	cargoDetail?: Models.CargoDetailEntity | Models.ICargoDetailEntityAttributes;
	customTicketTypeId?: string;
	customTicketType?: Models.CustomTicketTypeEntity | Models.ICustomTicketTypeEntityAttributes;
	freightDetailId?: string;
	freightDetail?: Models.FreightDetailEntity | Models.IFreightDetailEntityAttributes;
	passengerDetails: Array<Models.PassengerDetailTicket | Models.IPassengerDetailTicketAttributes>;
	towOnDetailId?: string;
	towOnDetail?: Models.TowOnDetailEntity | Models.ITowOnDetailEntityAttributes;
	configuration?: string;
}
@entity('TicketEntity', 'Ticket')
export default class TicketEntity extends Model implements ITicketEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<TicketEntity, number>()
	public price: number;

	/**
	 * Quantity
	 */
	@observable
	@attribute<TicketEntity, number>()
	public amount: number;

	@observable
	@attribute()
	public additionalBookingOptionId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public additionalBookingOption: Models.AdditionalBookingOptionEntity;

	@observable
	@attribute()
	public alterationId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public alteration: Models.AlterationEntity;

	@observable
	@attribute()
	public cargoDetailId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public cargoDetail: Models.CargoDetailEntity;

	@observable
	@attribute()
	public customTicketTypeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public customTicketType: Models.CustomTicketTypeEntity;

	@observable
	@attribute()
	public freightDetailId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightDetail: Models.FreightDetailEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public passengerDetails: Models.PassengerDetailTicket[] = [];

	@observable
	@attribute()
	public towOnDetailId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public towOnDetail: Models.TowOnDetailEntity;

	@observable
	@attribute<TicketEntity, string>()
	public configuration: string;

	constructor(attributes?: Partial<ITicketEntityAttributes>) {
		super(attributes);

		this.passengerDetails = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ITicketEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.price !== undefined) {
				this.price = attributes.price;
			}
			if (attributes.amount !== undefined) {
				this.amount = attributes.amount;
			}
			if (attributes.additionalBookingOptionId !== undefined) {
				this.additionalBookingOptionId = attributes.additionalBookingOptionId;
			}
			if (attributes.additionalBookingOption !== undefined) {
				if (attributes.additionalBookingOption === null) {
					this.additionalBookingOption = attributes.additionalBookingOption;
				} else if (attributes.additionalBookingOption instanceof Models.AdditionalBookingOptionEntity) {
					this.additionalBookingOption = attributes.additionalBookingOption;
					this.additionalBookingOptionId = attributes.additionalBookingOption.id;
				} else {
					this.additionalBookingOption = new Models.AdditionalBookingOptionEntity(
						attributes.additionalBookingOption,
					);
					this.additionalBookingOptionId = this.additionalBookingOption.id;
				}
			}
			if (attributes.alterationId !== undefined) {
				this.alterationId = attributes.alterationId;
			}
			if (attributes.alteration !== undefined) {
				if (attributes.alteration === null) {
					this.alteration = attributes.alteration;
				} else if (attributes.alteration instanceof Models.AlterationEntity) {
					this.alteration = attributes.alteration;
					this.alterationId = attributes.alteration.id;
				} else {
					this.alteration = new Models.AlterationEntity(attributes.alteration);
					this.alterationId = this.alteration.id;
				}
			}
			if (attributes.cargoDetailId !== undefined) {
				this.cargoDetailId = attributes.cargoDetailId;
			}
			if (attributes.cargoDetail !== undefined) {
				if (attributes.cargoDetail === null) {
					this.cargoDetail = attributes.cargoDetail;
				} else if (attributes.cargoDetail instanceof Models.CargoDetailEntity) {
					this.cargoDetail = attributes.cargoDetail;
					this.cargoDetailId = attributes.cargoDetail.id;
				} else {
					this.cargoDetail = new Models.CargoDetailEntity(attributes.cargoDetail);
					this.cargoDetailId = this.cargoDetail.id;
				}
			}
			if (attributes.customTicketTypeId !== undefined) {
				this.customTicketTypeId = attributes.customTicketTypeId;
			}
			if (attributes.customTicketType !== undefined) {
				if (attributes.customTicketType === null) {
					this.customTicketType = attributes.customTicketType;
				} else if (attributes.customTicketType instanceof Models.CustomTicketTypeEntity) {
					this.customTicketType = attributes.customTicketType;
					this.customTicketTypeId = attributes.customTicketType.id;
				} else {
					this.customTicketType = new Models.CustomTicketTypeEntity(attributes.customTicketType);
					this.customTicketTypeId = this.customTicketType.id;
				}
			}
			if (attributes.freightDetailId !== undefined) {
				this.freightDetailId = attributes.freightDetailId;
			}
			if (attributes.freightDetail !== undefined) {
				if (attributes.freightDetail === null) {
					this.freightDetail = attributes.freightDetail;
				} else if (attributes.freightDetail instanceof Models.FreightDetailEntity) {
					this.freightDetail = attributes.freightDetail;
					this.freightDetailId = attributes.freightDetail.id;
				} else {
					this.freightDetail = new Models.FreightDetailEntity(attributes.freightDetail);
					this.freightDetailId = this.freightDetail.id;
				}
			}
			if (attributes.passengerDetails !== undefined && Array.isArray(attributes.passengerDetails)) {
				if (!this.passengerDetails) {
					this.passengerDetails = [];
				}
				for (const model of attributes.passengerDetails) {
					if (model instanceof Models.PassengerDetailTicket) {
						this.passengerDetails.push(model);
					} else {
						this.passengerDetails.push(new Models.PassengerDetailTicket(model));
					}
				}
			}
			if (attributes.towOnDetailId !== undefined) {
				this.towOnDetailId = attributes.towOnDetailId;
			}
			if (attributes.towOnDetail !== undefined) {
				if (attributes.towOnDetail === null) {
					this.towOnDetail = attributes.towOnDetail;
				} else if (attributes.towOnDetail instanceof Models.TowOnDetailEntity) {
					this.towOnDetail = attributes.towOnDetail;
					this.towOnDetailId = attributes.towOnDetail.id;
				} else {
					this.towOnDetail = new Models.TowOnDetailEntity(attributes.towOnDetail);
					this.towOnDetailId = this.towOnDetail.id;
				}
			}
			if (attributes.configuration) {
				this.configuration = attributes.configuration;
			}
		}
	}
}
